import React, { useState } from "react";
import { Trash2, Tag as TagIcon, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../ui/alert-dialog";

const MAX_NAMES_SHOWN = 4;

/**
 * Sticky selection toolbar shown above the documents table once at least
 * one row is checked. Two bulk actions — move-to-trash and add-tags — both
 * mirror single-document mutations already available from the per-row
 * kebab menu, applied to every selected document in one request.
 */
export default function BulkActionsBar({ selectedIds, docs, onClear, onDone }) {
  const { t } = useTranslation("documents");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingTags, setPendingTags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");

  const count = selectedIds.size;
  if (count === 0) return null;

  const selectedDocs = docs.filter((d) => selectedIds.has(d.id));
  const ids = Array.from(selectedIds);

  const namesPreview = () => {
    const names = selectedDocs.slice(0, MAX_NAMES_SHOWN).map((d) => `"${d.filename}"`).join(", ");
    const extra = selectedDocs.length - MAX_NAMES_SHOWN;
    return extra > 0 ? `${names} ${t("bulk.delete_dialog_more", { count: extra })}` : names;
  };

  const reportResult = (successKey, count_, skippedCount) => {
    let msg = t(successKey, { count: count_ });
    if (skippedCount > 0) {
      msg += t("bulk.result_with_skipped", { skipped: skippedCount });
    }
    toast.success(msg);
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      const res = await api.post("/documents/bulk-delete", { document_ids: ids });
      reportResult("bulk.delete_success", res.data.deleted_count, res.data.skipped_count);
      setDeleteOpen(false);
      onDone?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      if (status === 403) toast.error(t("bulk.delete_forbidden"));
      else toast.error(`${t("bulk.error_generic")} — ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const addPendingTag = () => {
    const val = tagDraft.trim();
    setTagDraft("");
    if (!val) return;
    if (pendingTags.some((x) => x.toLowerCase() === val.toLowerCase())) return;
    setPendingTags((prev) => [...prev, val]);
  };

  const removePendingTag = (tag) => {
    setPendingTags((prev) => prev.filter((x) => x !== tag));
  };

  const handleAddTags = async () => {
    if (pendingTags.length === 0) return;
    setBusy(true);
    try {
      const res = await api.post("/documents/bulk-add-tags", {
        document_ids: ids,
        tags: pendingTags,
      });
      reportResult("bulk.add_tags_success", res.data.updated_count, res.data.skipped_count);
      setPendingTags([]);
      setTagsOpen(false);
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`${t("bulk.error_generic")} — ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="bulk-actions-bar"
      className="sticky top-0 z-10 flex flex-wrap items-center gap-3 px-5 py-3 bg-qenva-navy text-white border-b border-zinc-100"
    >
      <span className="text-[13px] font-medium" data-testid="bulk-selected-count">
        {t("bulk.selected_count", { count })}
      </span>

      <Button
        type="button"
        onClick={() => setDeleteOpen(true)}
        data-testid="bulk-delete-button"
        className="h-8 px-3 bg-white/10 hover:bg-white/20 text-white text-[12.5px]"
      >
        <Trash2 size={13} className="mr-1.5" />
        {t("bulk.delete_cta")}
      </Button>

      <Popover open={tagsOpen} onOpenChange={setTagsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            data-testid="bulk-add-tags-button"
            className="h-8 px-3 bg-white/10 hover:bg-white/20 text-white text-[12.5px]"
          >
            <TagIcon size={13} className="mr-1.5" />
            {t("bulk.add_tags_cta")}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {pendingTags.map((tag) => (
              <span
                key={tag}
                data-testid={`bulk-pending-tag-${tag}`}
                className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-700"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removePendingTag(tag)}
                  aria-label={t("bulk.add_tags_remove_pending", { tag })}
                  className="text-zinc-400 hover:text-zinc-900"
                >
                  <X size={10} strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>
          <input
            autoFocus
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addPendingTag();
              }
            }}
            placeholder={t("bulk.add_tags_input_placeholder")}
            data-testid="bulk-add-tags-input"
            className="w-full h-8 px-2.5 text-xs rounded-md border border-zinc-300 focus:outline-none focus:ring-1 focus:ring-zinc-950"
          />
          <Button
            type="button"
            onClick={handleAddTags}
            disabled={busy || pendingTags.length === 0}
            data-testid="bulk-add-tags-apply"
            className="w-full h-8 mt-2 bg-zinc-950 hover:bg-zinc-800 text-white text-[12.5px] disabled:opacity-50"
          >
            {busy ? (
              <>
                <Loader2 size={13} className="mr-1.5 animate-spin" /> {t("bulk.add_tags_pending")}
              </>
            ) : (
              t("bulk.add_tags_apply")
            )}
          </Button>
        </PopoverContent>
      </Popover>

      <button
        type="button"
        onClick={onClear}
        data-testid="bulk-clear-selection"
        className="text-[12.5px] text-white/70 hover:text-white underline underline-offset-2 ml-auto"
      >
        {t("bulk.clear_selection")}
      </button>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("bulk.delete_dialog_title", { count })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("bulk.delete_dialog_body", { names: namesPreview() })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="bulk-delete-cancel">
              {t("bulk.delete_dialog_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={busy}
              data-testid="bulk-delete-confirm-btn"
              className="bg-qenva-cat-contract hover:bg-qenva-cat-contract/90 text-white"
            >
              {busy ? (
                <>
                  <Loader2 size={13} className="mr-1 animate-spin" /> {t("bulk.delete_dialog_pending")}
                </>
              ) : (
                t("bulk.delete_dialog_confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
